// src/components/common/CodeEditor.jsx
import React, { useEffect, useRef, useState } from 'react';

const CodeEditor = ({ 
  value, 
  onChange, 
  language = 'html', 
  placeholder = '',
  height = '200px',
  readOnly = false,
  darkMode = false // Add darkMode prop instead of context
}) => {
  const textareaRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [value]);

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  const handleCopy = () => {
    if (navigator.clipboard && value) {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleIndent = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = value.substring(0, start) + '  ' + value.substring(end);
    onChange(newValue);
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + 2;
    }, 0);
  };

  return (
    <div className={`relative rounded-lg overflow-hidden border ${
      darkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-300 bg-gray-50'
    }`}>
      {/* Editor header */}
      <div className={`flex justify-between items-center px-4 py-2 border-b ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'
      }`}>
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${
            darkMode ? 'bg-red-500' : 'bg-red-400'
          }`}></div>
          <div className={`w-3 h-3 rounded-full ${
            darkMode ? 'bg-yellow-500' : 'bg-yellow-400'
          }`}></div>
          <div className={`w-3 h-3 rounded-full ${
            darkMode ? 'bg-green-500' : 'bg-green-400'
          }`}></div>
        </div>
        <div className={`text-xs px-2 py-1 rounded ${
          darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
        }`}>
          {language.toUpperCase()}
        </div>
      </div>

      {/* Line numbers and editor */}
      <div className="flex" style={{ minHeight: height }}>
        {/* Line numbers */}
        <div className={`py-2 px-3 text-right select-none ${
          darkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'
        }`} style={{ minWidth: '50px' }}>
          {value.split('\n').map((_, i) => (
            <div key={i} className="text-xs leading-6">{i + 1}</div>
          ))}
        </div>

        {/* Textarea */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            readOnly={readOnly}
            className={`w-full h-full p-3 font-mono text-sm outline-none resize-none ${
              darkMode 
                ? 'bg-gray-900 text-gray-100 placeholder-gray-600' 
                : 'bg-white text-gray-900 placeholder-gray-400'
            } ${readOnly ? 'cursor-not-allowed' : ''}`}
            style={{ 
              minHeight: height,
              lineHeight: '1.5',
              tabSize: 2
            }}
            spellCheck="false"
          />
          
          {/* Character count */}
          {!readOnly && (
            <div className={`absolute bottom-2 right-2 text-xs px-2 py-1 rounded ${
              darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
            }`}>
              {value.length} chars
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      {!readOnly && (
        <div className={`flex justify-between items-center px-4 py-2 border-t ${
          darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'
        }`}>
          <div className="text-xs text-gray-500">
            Press Tab for indentation
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleIndent}
              className={`text-xs px-2 py-1 rounded ${
                darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              Indent
            </button>
            <button
              onClick={handleCopy}
              className={`text-xs px-2 py-1 rounded ${
                copied 
                  ? 'bg-green-500 text-white'
                  : darkMode 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeEditor;